import { useState, useEffect, useContext, useCallback, useRef } from 'react'
import { __ } from '@wordpress/i18n'
import SbUtils from '../../Utils/SbUtils'
import Button from '../Common/Button'
import EddDownloadSelector from '../Common/EddDownloadSelector'
import useEddValidation from '../../hooks/useEddValidation'

/**
 * Edit EDD Downloads Modal
 *
 * Allows editing the downloads included in a multi-download EDD source.
 *
 * @param {Object} props
 * @param {Object} props.source - The source being edited
 * @param {Function} props.onCancel - Called when modal is cancelled
 * @param {Function} props.onSuccess - Called after successful update
 */
const EditEddDownloadsModal = ( props ) => {
    const {
        sbCustomizer,
        editorNotification,
        editorTopLoader
    } = useContext( SbUtils.getCurrentContext() );

    // Parse source info with error handling
    let sourceInfo = null;
    try {
        sourceInfo = typeof props.source.info === 'string'
            ? JSON.parse( props.source.info )
            : props.source.info;
    } catch ( e ) {
        console.error( 'Failed to parse source info:', e );
        sourceInfo = {};
    }

    // Downloads state
    const [ allDownloads, setAllDownloads ] = useState( [] );
    const [ selectedDownloads, setSelectedDownloads ] = useState( [] );
    const [ isLoading, setIsLoading ] = useState( true );
    const [ isSearchingDownloads, setIsSearchingDownloads ] = useState( false );

    // Categories state
    const [ allCategories, setAllCategories ] = useState( [] );
    const [ selectedCategories, setSelectedCategories ] = useState( [] );
    const [ loadingCategories, setLoadingCategories ] = useState( true );

    // Tags state
    const [ allTags, setAllTags ] = useState( [] );
    const [ selectedTags, setSelectedTags ] = useState( [] );
    const [ loadingTags, setLoadingTags ] = useState( true );

    // Common state
    const [ isSaving, setIsSaving ] = useState( false );
    const [ validationError, setValidationError ] = useState( null );
    const [ sourceName, setSourceName ] = useState( sourceInfo?.source_name || props.source.name || '' );

    // EDD validation hook (handles zero reviews warning automatically)
    const { validateSelection } = useEddValidation( {
        selectedDownloads,
        selectedCategories,
        selectedTags,
        setValidationError
    } );

    // Refs to track if initial selection has been done (prevents re-selection on search)
    const initialDownloadsSelected = useRef( false );
    const initialCategoriesSelected = useRef( false );
    const initialTagsSelected = useRef( false );

    // Load downloads, categories, and tags on mount
    useEffect( () => {
        loadDownloads();
        loadCategories();
        loadTags();
    }, [] );

    // Pre-select downloads from source (only on initial load, not on search)
    useEffect( () => {
        if ( initialDownloadsSelected.current ) {
            return;
        }
        if ( allDownloads.length > 0 && Array.isArray( sourceInfo?.direct_download_ids ) ) {
            // Use direct_download_ids (user-selected downloads) rather than all resolved download_ids
            const preSelected = allDownloads.filter( download =>
                sourceInfo.direct_download_ids.includes( download.id )
            );
            setSelectedDownloads( preSelected );
            initialDownloadsSelected.current = true;
        } else if ( allDownloads.length > 0 && Array.isArray( sourceInfo?.download_ids ) ) {
            // Fallback for older sources that don't have direct_download_ids
            const preSelected = allDownloads.filter( download =>
                sourceInfo.download_ids.includes( download.id )
            );
            setSelectedDownloads( preSelected );
            initialDownloadsSelected.current = true;
        }
    }, [ allDownloads ] );

    // Pre-select categories from source (only on initial load, not on search)
    useEffect( () => {
        if ( initialCategoriesSelected.current ) {
            return;
        }
        if ( allCategories.length > 0 && Array.isArray( sourceInfo?.category_ids ) ) {
            const preSelected = allCategories.filter( category =>
                sourceInfo.category_ids.includes( category.id )
            );
            setSelectedCategories( preSelected );
            initialCategoriesSelected.current = true;
        }
    }, [ allCategories ] );

    // Pre-select tags from source (only on initial load, not on search)
    useEffect( () => {
        if ( initialTagsSelected.current ) {
            return;
        }
        if ( allTags.length > 0 && Array.isArray( sourceInfo?.tag_ids ) ) {
            const preSelected = allTags.filter( tag =>
                sourceInfo.tag_ids.includes( tag.id )
            );
            setSelectedTags( preSelected );
            initialTagsSelected.current = true;
        }
    }, [ allTags ] );

    const loadDownloads = useCallback( ( searchTerm = '' ) => {
        // Show skeleton loaders for both initial load and search
        setIsSearchingDownloads( true );
        const startTime = Date.now();
        const minDisplayTime = 500; // Minimum time to show skeleton loaders (ms)

        const formData = {
            action: 'sbr_get_edd_downloads',
            nonce: sbCustomizer?.nonce,
            search: searchTerm
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( response ) => {
                // Ensure skeleton shows for at least minDisplayTime
                const elapsed = Date.now() - startTime;
                const remainingTime = Math.max( 0, minDisplayTime - elapsed );

                setTimeout( () => {
                    setIsLoading( false );
                    setIsSearchingDownloads( false );
                    if ( response?.success && response?.data?.downloads ) {
                        setAllDownloads( response.data.downloads );
                    }
                }, remainingTime );
            },
            editorTopLoader,
            () => {
                setIsLoading( false );
                setIsSearchingDownloads( false );
                SbUtils.applyNotification( {
                    icon: 'error',
                    text: __( 'Failed to load downloads. Please try again.', 'sb-customizer' )
                }, editorNotification );
            },
            null
        );
    }, [ sbCustomizer?.ajaxHandler, sbCustomizer?.nonce, editorTopLoader ] );

    const loadCategories = () => {
        setLoadingCategories( true );
        const startTime = Date.now();
        const minDisplayTime = 500;

        const formData = {
            action: 'sbr_get_edd_categories',
            nonce: sbCustomizer?.nonce
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( response ) => {
                const elapsed = Date.now() - startTime;
                const remainingTime = Math.max( 0, minDisplayTime - elapsed );

                setTimeout( () => {
                    setLoadingCategories( false );
                    if ( response?.success && response?.data?.categories ) {
                        setAllCategories( response.data.categories );
                    }
                }, remainingTime );
            },
            null,
            () => {
                setLoadingCategories( false );
                SbUtils.applyNotification( {
                    icon: 'error',
                    text: __( 'Failed to load categories. Please try again.', 'sb-customizer' )
                }, editorNotification );
            },
            null
        );
    };

    const loadTags = () => {
        setLoadingTags( true );
        const startTime = Date.now();
        const minDisplayTime = 500;

        const formData = {
            action: 'sbr_get_edd_tags',
            nonce: sbCustomizer?.nonce
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( response ) => {
                const elapsed = Date.now() - startTime;
                const remainingTime = Math.max( 0, minDisplayTime - elapsed );

                setTimeout( () => {
                    setLoadingTags( false );
                    if ( response?.success && response?.data?.tags ) {
                        setAllTags( response.data.tags );
                    }
                }, remainingTime );
            },
            null,
            () => {
                setLoadingTags( false );
                SbUtils.applyNotification( {
                    icon: 'error',
                    text: __( 'Failed to load tags. Please try again.', 'sb-customizer' )
                }, editorNotification );
            },
            null
        );
    };

    const handleDownloadToggle = ( download ) => {
        setValidationError( null );
        const isSelected = selectedDownloads.some( d => d.id === download.id );
        if ( isSelected ) {
            setSelectedDownloads( selectedDownloads.filter( d => d.id !== download.id ) );
        } else {
            setSelectedDownloads( [ ...selectedDownloads, download ] );
        }
    };

    const handleCategoryToggle = ( category ) => {
        setValidationError( null );
        const isSelected = selectedCategories.some( c => c.id === category.id );
        if ( isSelected ) {
            setSelectedCategories( selectedCategories.filter( c => c.id !== category.id ) );
        } else {
            setSelectedCategories( [ ...selectedCategories, category ] );
        }
    };

    const handleTagToggle = ( tag ) => {
        setValidationError( null );
        const isSelected = selectedTags.some( t => t.id === tag.id );
        if ( isSelected ) {
            setSelectedTags( selectedTags.filter( t => t.id !== tag.id ) );
        } else {
            setSelectedTags( [ ...selectedTags, tag ] );
        }
    };

    const handleSelectAll = ( filteredDownloads ) => {
        setValidationError( null );
        // Merge: add filtered downloads to existing selection (avoid duplicates)
        const existingIds = new Set( selectedDownloads.map( d => d.id ) );
        const newDownloads = filteredDownloads.filter( d => !existingIds.has( d.id ) );
        setSelectedDownloads( [ ...selectedDownloads, ...newDownloads ] );
    };

    const handleDeselectAll = ( filteredDownloads ) => {
        // Only deselect filtered/visible downloads, preserve non-visible selections
        const filteredIds = new Set( filteredDownloads.map( d => d.id ) );
        setSelectedDownloads( selectedDownloads.filter( d => !filteredIds.has( d.id ) ) );
    };

    const handleSelectAllCategories = ( filteredCategories ) => {
        setValidationError( null );
        // Merge: add filtered categories to existing selection (avoid duplicates)
        const existingIds = new Set( selectedCategories.map( c => c.id ) );
        const newCategories = filteredCategories.filter( c => !existingIds.has( c.id ) );
        setSelectedCategories( [ ...selectedCategories, ...newCategories ] );
    };

    const handleDeselectAllCategories = ( filteredCategories ) => {
        // Only deselect filtered/visible categories, preserve non-visible selections
        const filteredIds = new Set( filteredCategories.map( c => c.id ) );
        setSelectedCategories( selectedCategories.filter( c => !filteredIds.has( c.id ) ) );
    };

    const handleSelectAllTags = ( filteredTags ) => {
        setValidationError( null );
        // Merge: add filtered tags to existing selection (avoid duplicates)
        const existingIds = new Set( selectedTags.map( t => t.id ) );
        const newTags = filteredTags.filter( t => !existingIds.has( t.id ) );
        setSelectedTags( [ ...selectedTags, ...newTags ] );
    };

    const handleDeselectAllTags = ( filteredTags ) => {
        // Only deselect filtered/visible tags, preserve non-visible selections
        const filteredIds = new Set( filteredTags.map( t => t.id ) );
        setSelectedTags( selectedTags.filter( t => !filteredIds.has( t.id ) ) );
    };

    const handleClearAll = () => {
        setSelectedDownloads( [] );
        setSelectedCategories( [] );
        setSelectedTags( [] );
    };

    const handleSave = () => {
        // Validate - at least one selection required
        const validationMsg = validateSelection();
        if ( validationMsg ) {
            setValidationError( validationMsg );
            return;
        }

        setIsSaving( true );

        // Build formData with arrays for PHP handling
        const formData = {
            action: 'sbr_update_edd_source_multi',
            nonce: sbCustomizer?.nonce,
            source_id: props.source.account_id,
            source_name: sourceName || __( 'EDD Downloads', 'sb-customizer' )
        };

        // Add download_ids as separate entries for PHP array handling
        selectedDownloads.forEach( ( download, index ) => {
            formData[ 'download_ids[' + index + ']' ] = download.id;
        });

        // Add category_ids as separate entries for PHP array handling
        selectedCategories.forEach( ( category, index ) => {
            formData[ 'category_ids[' + index + ']' ] = category.id;
        });

        // Add tag_ids as separate entries for PHP array handling
        selectedTags.forEach( ( tag, index ) => {
            formData[ 'tag_ids[' + index + ']' ] = tag.id;
        });

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( response ) => {
                setIsSaving( false );
                if ( response?.success ) {
                    // Don't update sources list from response - parent will refresh for current page
                    // This prevents showing page 1 results while staying on current page

                    SbUtils.applyNotification( {
                        icon: 'success',
                        text: __( 'Source updated successfully', 'sb-customizer' )
                    }, editorNotification );

                    if ( props.onSuccess ) {
                        props.onSuccess( response.source );
                    }
                } else {
                    SbUtils.applyNotification( {
                        icon: 'error',
                        text: response?.message || __( 'Failed to update source', 'sb-customizer' )
                    }, editorNotification );
                }
            },
            editorTopLoader,
            () => {
                setIsSaving( false );
                SbUtils.applyNotification( {
                    icon: 'error',
                    text: __( 'Network error. Please check your connection and try again.', 'sb-customizer' )
                }, editorNotification );
            },
            null
        );
    };

    return (
        <div className='sb-addsource-modal sb-edit-woo-modal sb-fs'>
            <div className='sb-addsource-modal-heading sb-fs'>
                <h4 className='sb-h4'>{ __( 'Edit Downloads', 'sb-customizer' ) }</h4>
            </div>

            <div className='sb-addsource-modal-content sb-fs'>
                {/* Source Name */}
                <div className='sb-woo-source-name-section'>
                    <label className='sb-text-small sb-bold'>{ __( 'Source Name', 'sb-customizer' ) }</label>
                    <input
                        type='text'
                        className='sb-woo-source-name-input'
                        placeholder={ __( 'Enter a name for this source', 'sb-customizer' ) }
                        value={ sourceName }
                        onChange={ ( e ) => setSourceName( e.target.value ) }
                    />
                </div>

                {/* Shared Download Selector Component with Tabs */}
                <EddDownloadSelector
                    showTabs={ true }
                    downloads={ allDownloads }
                    selectedDownloads={ selectedDownloads }
                    onDownloadToggle={ handleDownloadToggle }
                    onSelectAll={ handleSelectAll }
                    onDeselectAll={ handleDeselectAll }
                    isLoading={ isLoading }
                    validationError={ validationError }
                    categories={ allCategories }
                    selectedCategories={ selectedCategories }
                    onCategoryToggle={ handleCategoryToggle }
                    onSelectAllCategories={ handleSelectAllCategories }
                    onDeselectAllCategories={ handleDeselectAllCategories }
                    loadingCategories={ loadingCategories }
                    tags={ allTags }
                    selectedTags={ selectedTags }
                    onTagToggle={ handleTagToggle }
                    onSelectAllTags={ handleSelectAllTags }
                    onDeselectAllTags={ handleDeselectAllTags }
                    loadingTags={ loadingTags }
                    onClearAll={ handleClearAll }
                    onSearchDownloads={ loadDownloads }
                    isSearching={ isSearchingDownloads }
                />
            </div>

            <div className='sb-addsource-modal-btns sb-fs'>
                <Button
                    size='medium'
                    type='secondary'
                    text={ __( 'Cancel', 'sb-customizer' ) }
                    onClick={ props.onCancel }
                />
                <Button
                    size='medium'
                    type='primary'
                    text={ isSaving ? __( 'Saving...', 'sb-customizer' ) : __( 'Finish', 'sb-customizer' ) }
                    icon={ isSaving ? 'spinner' : 'chevron-right' }
                    iconPosition='right'
                    onClick={ handleSave }
                    disabled={ isSaving }
                />
            </div>
        </div>
    );
};

export default EditEddDownloadsModal;
